import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { chatAPI, giftAPI, matchAPI, notificationAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import LoadingAnimation from "../components/LoadingAnimation";
import UserProfileModal from "../components/UserProfileModal";
import AuthBackground from "../components/AuthBackground";
import { initSocket, setUserId, joinRoom, on, emit } from "../services/socket";
import { setCookie } from "../utils/cookieHelper";
import { cacheService } from "../services/cache";
import { useCleanupOnUnload } from "../hooks/useCleanupOnUnload";

const debugLog = () => {};

const extractSenderId = (sender) => {
  if (!sender) return "";
  if (typeof sender === "string") return sender;
  if (typeof sender === "object") {
    return sender._id || sender.id || sender.toString?.() || "";
  }
  return "";
};

const normalizeMessage = (message) => {
  const senderIdString = extractSenderId(message.senderId)?.toString() || "";
  return {
    ...message,
    senderIdString,
  };
};

const notificationsMatchId = (notification) => {
  if (!notification) return "";
  if (typeof notification.matchId === "string") return notification.matchId;
  if (notification.matchId?._id) return notification.matchId._id.toString();
  return notification.matchId?.toString?.() || "";
};

// Helper: Extract userId from JWT token (doesn't rely on React context)
const extractUserIdFromToken = () => {
  try {
    const token = sessionStorage.getItem("token");
    if (!token) return null;

    // JWT format: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    // Decode payload
    const payload = JSON.parse(atob(parts[1]));
    return payload.userId || payload.data?.userId || null;
  } catch (error) {
    console.error("[Chat] Error extracting userId from token:", error);
    return null;
  }
};

function Chat() {
  const { matchId } = useParams();
  const {
    user,
    setActiveMatchId,
    markMatchRead,
    updateUser,
    unreadByMatch,
    refreshNotificationCount,
  } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const unsubscribeRef = useRef(null);
  const lastLoadedRef = useRef({ matchId: null, loadedAt: 0 });
  const loadInFlightRef = useRef(false);
  const lastMessageIdRef = useRef(null);

  // Clear localStorage when tab is closed
  useCleanupOnUnload();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [otherUserId, setOtherUserId] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({ special: 0, normal: 0 });
  const [showGifts, setShowGifts] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [selectedGift, setSelectedGift] = useState(null);
  const [giftMessage, setGiftMessage] = useState("");
  const [resolvedUserId, setResolvedUserId] = useState(null);
  const [sendingGift, setSendingGift] = useState(false);
  const [giftError, setGiftError] = useState(null);
  const [coinBalance, setCoinBalance] = useState(user?.coins || 0);
  const [giftsLoading, setGiftsLoading] = useState(false);
  const [giftsError, setGiftsError] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const normalizedOtherUserId =
    otherUserId?.toString?.() || otherUserId || null;

  const markNotificationsReadForMatch = useCallback(
    async (targetMatchId) => {
      if (!targetMatchId) return;
      try {
        const response = await notificationAPI.getNotifications();
        const items = response.data.data || [];
        const matchIdString = targetMatchId.toString();
        const ids = items
          .filter(
            (item) =>
              item.type === "message" &&
              !item.isRead &&
              notificationsMatchId(item) === matchIdString,
          )
          .map((item) => item._id);

        if (ids.length > 0) {
          await notificationAPI.markAsRead(ids);
          refreshNotificationCount();
        }
      } catch (error) {
        console.error("[Chat] Error marking notifications read:", error);
      }
    },
    [refreshNotificationCount],
  );

  const contextUserId = user?.userId ? user.userId.toString() : "";

  // Resolve userId from context or token fallback
  useEffect(() => {
    if (contextUserId) {
      setResolvedUserId(contextUserId);
    } else {
      const tokenUserId = extractUserIdFromToken();
      if (tokenUserId) {
        setResolvedUserId(tokenUserId);
      }
    }
  }, [contextUserId]);

  // Update unread counts in real-time from context (top-level, with correct dependencies)
  useEffect(() => {
    if (!matchId || !unreadByMatch) return;
    // Only update unread badge, do NOT reload messages
    setUnreadCounts((prev) => {
      const special = unreadByMatch[matchId]?.unreadSpecialCount || 0;
      const normal = unreadByMatch[matchId]?.unreadNormalCount || 0;
      if (prev.special !== special || prev.normal !== normal) {
        return { special, normal };
      }
      return prev;
    });
  }, [matchId, unreadByMatch]);

  const loadMatchAndMessages = useCallback(async () => {
    if (!matchId) return;
    const now = Date.now();
    const lastLoad = lastLoadedRef.current;

    if (
      loadInFlightRef.current &&
      lastLoad.matchId?.toString() === matchId.toString()
    ) {
      return;
    }

    if (
      lastLoad.matchId?.toString() === matchId.toString() &&
      now - lastLoad.loadedAt < 2000
    ) {
      // Try to load from cache while throttled
      const cachedMessages = cacheService.get(`messages_${matchId}`);
      const cachedMatch = cacheService.get(`match_${matchId}`);

      if (cachedMessages && cachedMatch) {
        setMessages(cachedMessages);
        setOtherUserId(cachedMatch.receiverId);
        setOtherUser(cachedMatch.profile);
        setLoading(false);
      }
      return;
    }

    loadInFlightRef.current = true;
    try {
      debugLog("[Chat] Loading match and messages for matchId:", matchId);
      const matchResponse = await matchAPI.getMatchById(matchId);
      const { otherUserId: receiverId, profile } = matchResponse.data.data;

      setOtherUserId(receiverId);
      setOtherUser(profile);
      // Set initial unread counts from context if available
      // Unread counts are now handled by useEffect at the top level

      const messagesResponse = await chatAPI.getMessages(matchId);
      const normalizedMessages =
        messagesResponse.data.data.map(normalizeMessage);
      setMessages(normalizedMessages);

      // Mark messages as read for this chat
      try {
        await chatAPI.markChatAsRead(matchId);
      } catch (markReadError) {
        console.error("[Chat] Error marking messages as read:", markReadError);
      }

      // Mark message notifications as read for this chat
      await markNotificationsReadForMatch(matchId);

      // Cache match data and messages (5 minutes)
      cacheService.set(
        `match_${matchId}`,
        { receiverId, profile },
        { ttl: 5 * 60 * 1000, useSessionStorage: true },
      );

      cacheService.set(`messages_${matchId}`, normalizedMessages, {
        ttl: 5 * 60 * 1000,
        useSessionStorage: true,
      });

      lastLoadedRef.current = {
        matchId: matchId.toString(),
        loadedAt: Date.now(),
      };

      debugLog("[Chat] Match and messages loaded successfully");
    } catch (error) {
      // Backend validation failed - user doesn't have access to this match
      if (error.response?.status === 403 || error.response?.status === 404) {
        console.error("[Chat] Access denied to match:", error.response?.status);
        navigate("/chats", { replace: true });
      } else {
        console.error("[Chat] Error loading match/messages:", error);
      }
    } finally {
      loadInFlightRef.current = false;
      setLoading(false);
    }
  }, [matchId, navigate, markNotificationsReadForMatch]);

  const loadGifts = useCallback(async () => {
    try {
      // Try cache first
      const cachedGifts = cacheService.get("gifts");
      if (cachedGifts) {
        setGifts(cachedGifts);
        setGiftsLoading(false);
        setGiftsError(null);
        return;
      }

      setGiftsLoading(true);
      setGiftsError(null);
      const response = await Promise.race([
        giftAPI.getGifts(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Request timed out")), 8000),
        ),
      ]);

      const giftsData = response.data.data;
      setGifts(giftsData);

      // Cache gifts for 30 minutes (doesn't change often)
      cacheService.set("gifts", giftsData, {
        ttl: 30 * 60 * 1000,
        useSessionStorage: true,
      });
    } catch (error) {
      console.error("[Chat] Error loading gifts:", error);
      setGiftsError(
        error.response?.data?.message ||
          error.message ||
          "Unable to load gifts",
      );
    } finally {
      setGiftsLoading(false);
    }
  }, []);

  // Backend validates access in loadMatchAndMessages() and redirects if denied

  useEffect(() => {
    if (!matchId) return;
    const id = matchId.toString();
    setActiveMatchId(id);

    return () => {
      setActiveMatchId(null);
    };
  }, [matchId, setActiveMatchId]);

  // Mark match as read (don't block on verification)
  useEffect(() => {
    if (!matchId) return;
    // Only mark as read when entering chat, not on unread/context changes
    markMatchRead(matchId.toString());
  }, [matchId, markMatchRead]);

  // EFFECT 1: Initialize singleton socket and set userId
  useEffect(() => {
    debugLog("[Chat] Initializing socket singleton");

    // Initialize socket (singleton - safe to call multiple times)
    initSocket();

    // Set userId using resolved value (context or token fallback)
    if (resolvedUserId) {
      debugLog("[Chat] Setting userId:", resolvedUserId);
      setUserId(resolvedUserId);
    }
  }, [resolvedUserId]);

  // EFFECT 2: Register newMessage listener
  useEffect(() => {
    debugLog("[Chat] Registering newMessage listener");

    const handleNewMessage = (message) => {
      const normalized = normalizeMessage(message);
      // For special messages and gift messages, always show them to both sender and receiver
      // For normal messages, filter out sender's own messages (they're added optimistically)
      if (
        !message.isSpecial &&
        !message.isGift &&
        normalized.senderIdString === resolvedUserId
      ) {
        return;
      }

      // Mark message as read immediately if chat is open
      if (normalized.receiverIdString === resolvedUserId) {
        chatAPI.markChatAsRead(matchId).catch((err) => {
          console.error("[Chat] Error marking new message as read:", err);
        });
        markNotificationsReadForMatch(matchId);
      }

      // Keep the read status from the database
      // When chat is open, backend will auto-mark as read on getMessages()
      setMessages((prevMessages) => {
        // Prevent duplicates - check if message already exists
        const exists = prevMessages.some((msg) => msg._id === normalized._id);
        if (exists) return prevMessages;
        return [...prevMessages, normalized];
      });
    };

    // Register listener using singleton (prevents duplicates)
    unsubscribeRef.current = on("newMessage", handleNewMessage);

    // Cleanup listener on unmount
    return () => {
      debugLog("[Chat] Unregistering newMessage listener");
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [resolvedUserId, matchId, markNotificationsReadForMatch]);

  // EFFECT 2B: Register gift and wallet listeners
  useEffect(() => {
    debugLog("[Chat] Registering gift and wallet listeners");

    const handleGiftReceived = (data) => {
      debugLog("[Chat] Gift received:", data);
      // Optionally show a toast or notification
      // Only reload messages if gift is relevant to this chat
      if (data.matchId?.toString() === matchId?.toString()) {
        loadMatchAndMessages();
      }
    };

    const handleWalletUpdated = (data) => {
      debugLog("[Chat] Wallet updated:", data);
      setCoinBalance(data.coins);

      // Also update the user context to keep it in sync
      if (data.coins !== undefined) {
        updateUser({ coins: data.coins });
      }
    };

    const unsubscribeGift = on("giftReceived", handleGiftReceived);
    const unsubscribeWallet = on("walletUpdated", handleWalletUpdated);

    return () => {
      debugLog("[Chat] Unregistering gift and wallet listeners");
      if (unsubscribeGift) unsubscribeGift();
      if (unsubscribeWallet) unsubscribeWallet();
    };
  }, [updateUser, loadMatchAndMessages, matchId]);

  // EFFECT 3: Join room when matchId becomes available
  useEffect(() => {
    if (!matchId) {
      debugLog("[Chat] Waiting: matchId not available");
      return;
    }

    debugLog("[Chat] Joining room:", matchId);
    joinRoom(matchId);
    setCookie("activeMatchId", matchId.toString(), 1);
    emit("setActiveChat", { matchId: matchId.toString() });

    // Do not leave room here.
    // Rooms are kept joined globally so unread updates work after closing chat.
    return () => {
      emit("clearActiveChat");
    };
  }, [matchId]);

  // EFFECT 4: Load match/messages when user is available (backend validates access)
  useEffect(() => {
    // Only load messages when matchId changes, not on context/unread updates
    if (!resolvedUserId || !matchId) {
      debugLog("[Chat] Waiting: user or matchId not available");
      return;
    }
    debugLog("[Chat] Loading match and messages");
    setLoading(true);
    loadMatchAndMessages();
    // Do NOT reload messages on unread/context changes
  }, [matchId, resolvedUserId, loadMatchAndMessages]);

  const handleToggleGifts = useCallback(() => {
    const nextShow = !showGifts;
    setShowGifts(nextShow);
    if (nextShow && gifts.length === 0 && !giftsLoading) {
      loadGifts();
    }
  }, [showGifts, gifts.length, giftsLoading, loadGifts]);

  const handleOpenProfileModal = useCallback(() => {
    if (!normalizedOtherUserId) return;
    setShowProfileModal(true);
  }, [normalizedOtherUserId]);

  const handleCloseProfileModal = useCallback(() => {
    setShowProfileModal(false);
  }, []);

  useEffect(() => {
    if (user?.coins !== undefined) {
      setCoinBalance(user.coins);
    }
  }, [user?.coins]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const handleMessagesScroll = useCallback((event) => {
    const target = event.currentTarget;
    const distanceFromBottom =
      target.scrollHeight - (target.scrollTop + target.clientHeight);
    shouldAutoScrollRef.current = distanceFromBottom < 80;
  }, []);

  useEffect(() => {
    if (!loading) {
      shouldAutoScrollRef.current = true;
      scrollToBottom("auto");
    }
  }, [loading, scrollToBottom]);

  useEffect(() => {
    if (!messages.length) return;
    const lastId = messages[messages.length - 1]?._id;
    if (lastMessageIdRef.current === lastId) return;
    lastMessageIdRef.current = lastId;

    if (shouldAutoScrollRef.current) {
      scrollToBottom("smooth");
    }
  }, [messages, scrollToBottom]);

  const handleSendMessage = useCallback(
    async (e) => {
      e.preventDefault();

      if (!newMessage.trim() || sending || !otherUserId) return;

      shouldAutoScrollRef.current = true;
      setSending(true);

      try {
        const response = await chatAPI.sendMessage({
          matchId,
          receiverId: otherUserId,
          content: newMessage.trim(),
        });

        setMessages((prevMessages) => [
          ...prevMessages,
          normalizeMessage(response.data.data),
        ]);
        setNewMessage("");
      } catch (error) {
        console.error("Error sending message:", error);
        alert(error.response?.data?.message || "Failed to send message");
      } finally {
        setSending(false);
      }
    },
    [newMessage, sending, otherUserId, matchId],
  );

  const handleSendGift = useCallback(
    async (giftId) => {
      try {
        if (!otherUserId) {
          setGiftError("Unable to send gift");
          return;
        }

        setSendingGift(true);
        setGiftError(null);

        const giftData = {
          giftId,
          receiverId: otherUserId,
          matchId,
        };

        // If a message was entered, add it to the gift data
        if (giftMessage.trim()) {
          giftData.message = giftMessage.trim();
        }

        const response = await giftAPI.sendGift(giftData);

        // Update coin balance immediately from response
        if (response.data.data.remainingCoins !== undefined) {
          setCoinBalance(response.data.data.remainingCoins);
          updateUser({ coins: response.data.data.remainingCoins });
        }

        // If a special message was created, the socket will broadcast it to both users
        // (including sender, since we modified handleNewMessage to accept special messages)

        setShowGifts(false);
        setSelectedGift(null);
        setGiftMessage("");
      } catch (error) {
        console.error("Error sending gift:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to send gift";
        setGiftError(errorMessage);
      } finally {
        setSendingGift(false);
      }
    },
    [otherUserId, giftMessage, matchId, updateUser],
  );

  const otherUserPrefill = otherUser
    ? {
        ...otherUser,
        userId: normalizedOtherUserId || otherUser.userId || otherUser._id,
      }
    : null;

  if (loading) {
    return (
      <>
        <LoadingAnimation isVisible={true} />
      </>
    );
  }

  return (
    <AuthBackground>
      <div className="h-screen max-h-screen overflow-hidden overflow-x-hidden pt-24 flex flex-col px-0 sm:px-4 md:px-6">
        <Navbar />

        <div className="w-full max-w-5xl mx-auto h-[calc(100vh-4rem)] flex flex-col bg-white rounded-none sm:rounded-t-2xl md:rounded-t-3xl shadow-2xl overflow-hidden">
          {/* Chat Header */}
          <div className="sticky top-0 z-20 flex items-center justify-between px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-gray-200 bg-white/95 backdrop-blur rounded-none sm:rounded-t-2xl md:rounded-t-3xl gap-2 sm:gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-900 font-semibold flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
            >
              ← <span className="hidden sm:inline">Back</span>
            </button>
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              {otherUser && (
                <button
                  type="button"
                  onClick={handleOpenProfileModal}
                  className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 text-left rounded-full px-1 py-1 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <img
                    src={otherUser.photos[0]}
                    alt={otherUser.name}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex flex-col min-w-0">
                    <h2 className="text-sm sm:text-lg font-bold text-gray-800 truncate">
                      {otherUser.name}
                    </h2>
                    {(unreadCounts.special > 0 || unreadCounts.normal > 0) && (
                      <div className="flex gap-1 mt-0.5">
                        {unreadCounts.special > 0 && (
                          <span className="bg-pink-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                            💖 {unreadCounts.special} special
                          </span>
                        )}
                        {unreadCounts.normal > 0 && (
                          <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                            {unreadCounts.normal} unread
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              )}
            </div>
            <button
              onClick={handleToggleGifts}
              className="text-xl sm:text-2xl hover:scale-110 transition-transform flex-shrink-0"
            >
              🎁
            </button>
          </div>

          {/* Messages Container - Fixed Height with Scroll */}
          <div
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 sm:px-4 md:px-6 py-4 space-y-3 bg-gradient-to-b from-slate-50 to-white"
          >
            {messages.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-400 text-center">
                  No messages yet. Say hi! 👋
                </p>
              </div>
            )}

            {messages.map((message) => {
              const isSent = message.senderIdString === resolvedUserId;

              // Render gift messages differently - show as large visual element
              if (message.isGift && message.giftData) {
                return (
                  <div
                    key={message._id}
                    className={`flex ${isSent ? "justify-end" : "justify-start"}`}
                  >
                    <div className="max-w-[85%] sm:max-w-[70%]">
                      <div
                        className={`rounded-2xl px-4 sm:px-6 py-4 sm:py-5 ${
                          isSent
                            ? message.isSpecial
                              ? "bg-gradient-to-r from-pink-500 to-pink-400"
                              : "bg-gradient-to-r from-purple-500 to-indigo-500"
                            : message.isSpecial
                              ? "bg-pink-50 border-2 border-pink-300"
                              : "bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-purple-200"
                        } shadow-lg text-center`}
                      >
                        <div className="text-6xl sm:text-7xl mb-2 animate-bounce">
                          {message.giftData.giftIcon}
                        </div>
                        <div
                          className={`text-sm sm:text-base font-bold mb-1 ${
                            isSent ? "text-white" : "text-gray-800"
                          }`}
                        >
                          {message.giftData.giftName}
                        </div>
                        {message.content !==
                          `Sent ${message.giftData.giftName}` && (
                          <div
                            className={`text-xs sm:text-sm mt-2 italic ${
                              isSent ? "text-white/90" : "text-gray-700"
                            }`}
                          >
                            "{message.content}"
                          </div>
                        )}
                        {message.isSpecial && (
                          <div
                            className={`text-xs font-bold mt-2 ${
                              isSent ? "text-white" : "text-pink-500"
                            }`}
                          >
                            💖 Special Gift
                          </div>
                        )}
                      </div>
                      <div
                        className={`text-xs text-gray-400 mt-1 ${
                          isSent ? "text-right" : "text-left"
                        }`}
                      >
                        {new Date(message.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              // Regular text messages
              return (
                <div
                  key={message._id}
                  className={`flex ${isSent ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-[75%] ${
                      message.isSpecial ? "sm:max-w-[80%]" : ""
                    }`}
                  >
                    {message.isSpecial && (
                      <div
                        className={`text-xs font-bold text-pink-500 mb-1 ${
                          isSent ? "text-right" : "text-left"
                        }`}
                      >
                        💖 Special
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-3 sm:px-4 py-2 ${
                        isSent
                          ? message.isSpecial
                            ? "bg-gradient-to-r from-pink-500 to-pink-400 text-white"
                            : "bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
                          : message.isSpecial
                            ? "bg-pink-50 border-2 border-pink-400 text-gray-800"
                            : "bg-white border border-gray-200 text-gray-800"
                      } shadow-sm`}
                    >
                      <p className="text-xs sm:text-sm break-words">
                        {message.content}
                      </p>
                    </div>
                    <div
                      className={`text-xs text-gray-400 mt-1 ${
                        isSent ? "text-right" : "text-left"
                      }`}
                    >
                      {new Date(message.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form
            onSubmit={handleSendMessage}
            className="sticky bottom-0 z-20 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] bg-white/95 backdrop-blur border-t border-gray-200"
          >
            <input
              type="text"
              placeholder="Message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-base sm:text-base border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm sm:text-base font-semibold rounded-full hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              <span className="hidden sm:inline">Send</span>
              <span className="sm:hidden">↓</span>
            </button>
          </form>

          {showGifts && (
            <div
              className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
              onClick={() => !sendingGift && setShowGifts(false)}
            >
              <div
                className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-lg w-full max-h-[85vh] sm:max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 text-gray-800">
                  Send a Gift
                </h3>

                {giftError && (
                  <div className="mb-4 p-3 sm:p-4 bg-red-50 border-2 border-red-400 rounded-lg">
                    <p className="text-sm sm:text-base text-red-600 font-semibold">
                      ❌ {giftError}
                    </p>
                  </div>
                )}

                {!selectedGift ? (
                  <div>
                    {giftsLoading && (
                      <div className="text-center text-sm sm:text-base text-gray-500 py-6">
                        Loading gifts...
                      </div>
                    )}

                    {!giftsLoading && giftsError && (
                      <div className="text-center text-sm sm:text-base text-red-600 py-6 space-y-3">
                        <div>{giftsError}</div>
                        <button
                          onClick={loadGifts}
                          className="px-4 py-2 text-xs sm:text-sm font-semibold text-indigo-600 bg-indigo-50 rounded-full hover:bg-indigo-100 transition"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {!giftsLoading && !giftsError && gifts.length === 0 && (
                      <div className="text-center text-sm sm:text-base text-gray-500 py-6">
                        No gifts available yet.
                      </div>
                    )}

                    {!giftsLoading && !giftsError && gifts.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                        {gifts.map((gift) => (
                          <div
                            key={gift._id}
                            onClick={() => {
                              if (coinBalance < gift.coinCost) return;
                              setSelectedGift(gift);
                              setGiftError(null);
                            }}
                            className={`text-center p-3 sm:p-5 border-2 rounded-lg sm:rounded-xl cursor-pointer transition-all ${
                              coinBalance >= gift.coinCost
                                ? "border-gray-200 hover:border-indigo-500 hover:scale-105"
                                : "border-gray-300 opacity-50 cursor-not-allowed"
                            }`}
                          >
                            <div className="text-3xl sm:text-4xl mb-2">
                              {gift.icon}
                            </div>
                            <div className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 line-clamp-1">
                              {gift.name}
                            </div>
                            <div className="text-xs text-indigo-600 font-semibold">
                              {gift.coinCost} coins
                            </div>
                            {coinBalance < gift.coinCost && (
                              <div className="text-xs text-red-500 mt-1 font-semibold">
                                Not enough
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="text-center p-4 sm:p-6 bg-gray-50 rounded-lg sm:rounded-xl">
                      <div className="text-5xl sm:text-6xl mb-3">
                        {selectedGift.icon}
                      </div>
                      <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">
                        {selectedGift.name}
                      </h4>
                      <p className="text-xs sm:text-sm text-indigo-600 font-semibold mb-2">
                        Cost: {selectedGift.coinCost} coins
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Your balance: {coinBalance} coins
                      </p>
                    </div>

                    {selectedGift.name === "Special Heart" && (
                      <div className="space-y-2">
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700">
                          Add a message (optional):
                        </label>
                        <textarea
                          placeholder="Tell them why they're special..."
                          value={giftMessage}
                          onChange={(e) => setGiftMessage(e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm border-2 border-gray-300 rounded-lg sm:rounded-xl resize-vertical min-h-[80px] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                    )}

                    <div className="flex gap-2 sm:gap-3">
                      <button
                        onClick={() => handleSendGift(selectedGift._id)}
                        disabled={
                          sendingGift || coinBalance < selectedGift.coinCost
                        }
                        className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {sendingGift ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            Sending...
                          </>
                        ) : (
                          "Send Gift"
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedGift(null);
                          setGiftMessage("");
                          setGiftError(null);
                        }}
                        disabled={sendingGift}
                        className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 text-gray-800 text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl hover:bg-gray-300 transition-all disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <UserProfileModal
            userId={normalizedOtherUserId}
            isOpen={showProfileModal}
            onClose={handleCloseProfileModal}
            prefetchedProfile={otherUserPrefill}
          />
        </div>
      </div>
    </AuthBackground>
  );
}

export default Chat;
